<script setup lang="ts">
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import type { Component } from "vue";
import { computed, ref, watch } from "vue";
import IconBulletedList from "@/components/forms/SharedComponents/WYSIWYG/IconBulletedList.vue";
import IconLink from "@/components/forms/SharedComponents/WYSIWYG/IconLink.vue";
import LinkModal from "@/components/forms/SharedComponents/WYSIWYG/LinkModal.vue";
import type { CommentEditorProps, TiptapDoc } from "../types";

interface ToolbarAction {
  key: string;
  title: string;
  label?: string;
  labelClass?: string;
  icon?: Component;
  separatorBefore?: boolean;
  isActive: () => boolean;
  run: () => void;
}

const props = withDefaults(defineProps<CommentEditorProps>(), {
  modelValue: undefined,
  placeholder: "",
  compact: false,
});

const emit = defineEmits<(e: "update:modelValue", value: TiptapDoc) => void>();

const isLinkModalOpen = ref<boolean>(false);
const previousUrl = ref<string>("");

const toEditorContent = (value: TiptapDoc | undefined): TiptapDoc | string =>
  value && Object.keys(value).length ? value : "";

const editor = useEditor({
  content: toEditorContent(props.modelValue),
  extensions: [StarterKit, Underline, Link.configure({ openOnClick: false })],
  editorProps: {
    attributes: {
      role: "textbox",
      "aria-multiline": "true",
      "aria-label": props.placeholder,
      "data-testid": "internal-comment-editor-content",
      class: [
        "outline-none overflow-y-auto max-h-[240px] px-3.5 py-3 text-sm leading-[1.55] text-indigo-base",
        "[&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-[22px] [&_ul]:my-1 [&_a]:text-teal-900 [&_a]:underline",
        props.compact ? "min-h-[72px]" : "min-h-[92px]",
      ].join(" "),
    },
  },
  onUpdate: ({ editor: instance }) => {
    emit("update:modelValue", instance.getJSON());
  },
});

watch(
  () => props.modelValue,
  (value) => {
    const instance = editor.value;
    if (instance && JSON.stringify(instance.getJSON()) !== JSON.stringify(value)) {
      instance.commands.setContent(toEditorContent(value), false);
    }
  },
);

const isEmpty = computed<boolean>(() => editor.value?.isEmpty ?? true);

const openLinkModal = (): void => {
  previousUrl.value = editor.value?.getAttributes("link").href ?? "";
  isLinkModalOpen.value = true;
};

const setLink = (url: string): void => {
  const chain = editor.value?.chain().focus().extendMarkRange("link");
  if (url) {
    chain?.setLink({ href: url }).run();
  } else {
    chain?.unsetLink().run();
  }
  isLinkModalOpen.value = false;
};

const toolbarActions = computed<ToolbarAction[]>(() => [
  {
    key: "bold",
    title: "Bold",
    label: "B",
    labelClass: "font-bold",
    isActive: () => !!editor.value?.isActive("bold"),
    run: () => editor.value?.chain().focus().toggleBold().run(),
  },
  {
    key: "italic",
    title: "Italic",
    label: "I",
    labelClass: "italic",
    isActive: () => !!editor.value?.isActive("italic"),
    run: () => editor.value?.chain().focus().toggleItalic().run(),
  },
  {
    key: "underline",
    title: "Underline",
    label: "U",
    labelClass: "underline",
    isActive: () => !!editor.value?.isActive("underline"),
    run: () => editor.value?.chain().focus().toggleUnderline().run(),
  },
  {
    key: "bulletList",
    title: "Bulleted list",
    icon: IconBulletedList,
    separatorBefore: true,
    isActive: () => !!editor.value?.isActive("bulletList"),
    run: () => editor.value?.chain().focus().toggleBulletList().run(),
  },
  {
    key: "link",
    title: "Add link",
    icon: IconLink,
    isActive: () => !!editor.value?.isActive("link"),
    run: openLinkModal,
  },
]);
</script>

<template>
  <div
    class="rounded-lg border border-gray-475 overflow-hidden transition-[border-color,box-shadow] duration-100 focus-within:border-teal-900 focus-within:ring-[3px] focus-within:ring-teal-light"
    data-testid="internal-comment-editor"
  >
    <div class="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-175 bg-blue-100">
      <template v-for="action in toolbarActions" :key="action.key">
        <span v-if="action.separatorBefore" class="w-px h-5 mx-1 bg-gray-175" />
        <button
          type="button"
          :title="action.title"
          :aria-label="action.title"
          :aria-pressed="action.isActive()"
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-sm transition-colors duration-100"
          :class="action.isActive() ? 'bg-teal-light text-teal-900' : 'text-gray-800 hover:bg-gray-10 hover:text-indigo-base'"
          :data-testid="`internal-comment-toolbar-${action.key}`"
          @mousedown.prevent
          @click="action.run"
        >
          <component :is="action.icon" v-if="action.icon" class="w-4 h-4 fill-current" />
          <span v-else :class="action.labelClass">{{ action.label }}</span>
        </button>
      </template>
    </div>
    <div class="relative">
      <span
        v-if="isEmpty && placeholder"
        class="absolute left-3.5 top-3 text-sm leading-[1.55] text-gray-500 pointer-events-none"
        data-testid="internal-comment-editor-placeholder"
      >
        {{ placeholder }}
      </span>
      <EditorContent :editor="editor" />
    </div>
    <slot />
    <LinkModal
      :open="isLinkModalOpen"
      :previous-url="previousUrl"
      @cancel="isLinkModalOpen = false"
      @set-url="setLink"
    />
  </div>
</template>
